import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  BrainCircuit, Sparkles, AlertTriangle, Award, GraduationCap, Shuffle,
  DollarSign, ArrowRight, TrendingUp, Settings2, RefreshCw, Star, Users,
  Camera, Download, FileText, FileSpreadsheet, CheckCircle2, Activity, Lock,
  Info,
} from 'lucide-react';
import {
  Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, LineChart, Line,
} from 'recharts';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useDataLabel } from '@/lib/dataI18n';
import SuiteDocSection from '@/components/admin/SuiteDocSection';

const QUADRANT_META = {
  star:        { label: 'intelligence.tiers.star',     tone: 'bg-success/10 text-success border-success/30',         color: 'hsl(var(--success))' },
  fairly_paid: { label: 'intelligence.tiers.fairly',   tone: 'bg-primary/10 text-primary border-primary/30',         color: 'hsl(var(--primary))' },
  steady:      { label: 'intelligence.tiers.steady',   tone: 'bg-muted text-muted-foreground border-border',         color: 'hsl(var(--muted-foreground))' },
  at_risk:     { label: 'intelligence.tiers.atRisk',   tone: 'bg-destructive/10 text-destructive border-destructive/30', color: 'hsl(var(--destructive))' },
};

const SUGGESTION_META = {
  promotion:         { icon: Award,        tone: 'success'     },
  bonus:             { icon: DollarSign,   tone: 'primary'     },
  salary_adjustment: { icon: TrendingUp,   tone: 'primary'     },
  risk:              { icon: AlertTriangle,tone: 'destructive' },
  training:          { icon: GraduationCap,tone: 'accent'      },
  reassignment:      { icon: Shuffle,      tone: 'info'        },
  succession:        { icon: Star,         tone: 'success'     },
  monitor:           { icon: Users,        tone: 'info'        },
};

const TONE = {
  success:     'bg-success/10 text-success border-success/20',
  primary:     'bg-primary/10 text-primary border-primary/20',
  destructive: 'bg-destructive/10 text-destructive border-destructive/20',
  accent:      'bg-accent/10 text-accent border-accent/20',
  info:        'bg-info/10 text-info border-info/20',
};

const fmtMoney = (n, ccy = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function AdminIntelligence() {
  const { t, i18n } = useTranslation();
  const { user: authUser } = useApp();
  const navigate = useNavigate();
  const isConsultant = authUser?.role === 'hm_advisor';
  const empSort = useSort();
  const tData = useDataLabel();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState(null);

  // Phase B
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');  // '' = all
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [actionsForUser, setActionsForUser] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [pendingAssignee, setPendingAssignee] = useState('');
  const [pendingDueDate, setPendingDueDate] = useState('');

  // Prompt 7: collaborator status (on_track | take_action | dismissed)
  const [collabStatus, setCollabStatus] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // Initial load: companies → first company
  useEffect(() => {
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos);
        if (cos.length) setCompanyId(cos[0].id);
      } catch (e) { toast.error(t('intelligence.loadError', { defaultValue: 'Failed to load companies' })); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When company changes, load departments + snapshots + assignees + reset filter
  useEffect(() => {
    if (!companyId) return;
    setDepartmentId('');
    (async () => {
      try {
        const [d, s, a] = await Promise.all([
          adminApi.listDepartments(companyId),
          adminApi.listSnapshots(companyId),
          adminApi.listAssignees(companyId),
        ]);
        setDepartments(d);
        setSnapshots(s);
        setAssignees(a);
      } catch (e) {/* tolerated */}
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await adminApi.intelligenceOverview(companyId, departmentId || null);
        setOverview(data);
        setNarrative(null);
      } catch (e) {
        toast.error(t('intelligence.overviewError', { defaultValue: 'Failed to load overview' }));
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, departmentId, t]);

  // Load actions for the drilldown employee (timeline)
  useEffect(() => {
    if (!drilldown || !companyId) { setActionsForUser([]); setCollabStatus(null); return; }
    (async () => {
      try {
        const list = await adminApi.listActions(companyId, { user_id: drilldown.user.id });
        setActionsForUser(list);
      } catch { setActionsForUser([]); }
      try {
        const s = await adminApi.getCollabStatus(drilldown.user.id);
        setCollabStatus(s);
      } catch { setCollabStatus({ status: 'on_track', history: [] }); }
    })();
  }, [drilldown, companyId]);

  const STATUS_LABEL = useMemo(() => ({
    on_track:    { en: 'On Track',    es: 'En curso' },
    take_action: { en: 'Take Action', es: 'Tomar Acción' },
    dismissed:   { en: 'Dismissed',   es: 'Descartada' },
  }), []);
  const localeKey = i18n.language?.startsWith('es') ? 'es' : 'en';
  const statusText = (k) => STATUS_LABEL[k]?.[localeKey] || k;

  const confirmStatusChange = async () => {
    if (!drilldown || !pendingStatus) return;
    setStatusSaving(true);
    try {
      const updated = await adminApi.updateCollabStatus(drilldown.user.id, pendingStatus);
      setCollabStatus(updated);
      toast.success(t('intelligence.statusUpdated', { defaultValue: 'Status updated' }));
      setPendingStatus(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally { setStatusSaving(false); }
  };

  const generateNarrative = async () => {
    if (!companyId) return;
    setNarrativeLoading(true);
    try {
      const r = await adminApi.intelligenceNarrative(companyId, departmentId || null);
      setNarrative(r);
    } catch (e) {
      toast.error(t('intelligence.narrativeError', { defaultValue: 'Failed to generate narrative' }));
    } finally { setNarrativeLoading(false); }
  };

  const captureSnapshot = async () => {
    if (!companyId) return;
    setSnapshotsLoading(true);
    try {
      const newSnap = await adminApi.createSnapshot(companyId);
      setSnapshots((prev) => [...prev, newSnap]);
      toast.success(t('intelligence.snapshotCaptured', { defaultValue: 'Snapshot captured' }));
    } catch (e) {
      toast.error(t('intelligence.snapshotError', { defaultValue: 'Snapshot failed' }));
    } finally { setSnapshotsLoading(false); }
  };

  const recordAction = async (suggestion, extras = {}) => {
    if (!companyId || !drilldown) return;
    try {
      const created = await adminApi.createAction(companyId, {
        user_id: drilldown.user.id,
        suggestion_type: suggestion.type,
        suggestion_title: suggestion.title,
        suggestion_detail: suggestion.detail,
        magnitude_pct: suggestion.magnitude_pct,
        ...extras,
      });
      setActionsForUser((prev) => [created, ...prev]);
      toast.success(t('intelligence.actionLogged', { defaultValue: 'Decision logged' }));
      return created;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const submitTakeAction = async () => {
    if (!pendingSuggestion) return;
    const extras = {};
    if (pendingAssignee) extras.assignee_user_id = pendingAssignee;
    if (pendingDueDate) extras.due_date = new Date(pendingDueDate).toISOString();
    await recordAction(pendingSuggestion, extras);
    setPendingSuggestion(null);
    setPendingAssignee('');
    setPendingDueDate('');
  };

  const dismissSuggestion = async (suggestion) => {
    const created = await recordAction(suggestion);
    if (!created) return;
    try {
      const upd = await adminApi.updateAction(companyId, created.id, { status: 'dismissed' });
      setActionsForUser((prev) => prev.map(a => a.id === created.id ? upd : a));
    } catch { /* tolerated */ }
  };

  const updateActionStatus = async (actionId, status) => {
    try {
      const upd = await adminApi.updateAction(companyId, actionId, { status });
      setActionsForUser((prev) => prev.map(a => a.id === actionId ? upd : a));
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
    } catch (e) { toast.error(t('toasts.failed')); }
  };

  const openConfig = async () => {
    if (!companyId) return;
    try {
      const cfg = await adminApi.intelligenceCompanyConfig(companyId);
      setConfigDraft(cfg);
      setConfigOpen(true);
    } catch (e) { toast.error(t('toasts.couldNotLoadConfig')); }
  };

  const saveConfig = async () => {
    try {
      await adminApi.saveIntelligenceCompanyConfig(companyId, {
        weights: configDraft.weights,
        tenure_years_ceiling: configDraft.tenure_years_ceiling,
        currency: configDraft.currency,
      });
      toast.success(t('intelligence.configSaved', { defaultValue: 'Configuration saved' }));
      setConfigOpen(false);
      // Re-fetch overview to apply new weights
      const data = await adminApi.intelligenceOverview(companyId, departmentId || null);
      setOverview(data);
      setNarrative(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const kpis = overview?.kpis;
  const employees = overview?.employees || [];
  const departmentRollup = overview?.department_rollup || [];
  const ccy = kpis?.currency || 'USD';
  const medianCost = kpis?.median_cost || 0;

  const scatterData = useMemo(() => employees.map(e => ({
    name: e.user.name,
    value: e.value.value_score,
    cost: e.cost.loaded,
    quadrant: e.quadrant,
    ratio: e.value.value_score && e.cost.loaded ? +(e.value.value_score / (e.cost.loaded / 1000)).toFixed(2) : 0,
  })), [employees]);

  return (
    <>
      <Topbar
        title={t('intelligence.title', { defaultValue: 'TalentIntelligence' })}
        subtitle={t('intelligence.subtitle', { defaultValue: 'Executive cross-suite value vs cost analysis' })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-44 lg:w-52" data-testid="intel-company-select">
                <SelectValue placeholder={t('intelligence.selectCompany', { defaultValue: 'Select company' })} />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`intel-company-opt-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={departmentId || '__all__'} onValueChange={(v)=>setDepartmentId(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-40 lg:w-48" data-testid="intel-dept-filter">
                <SelectValue placeholder={t('intelligence.allDepartments', { defaultValue: 'All departments' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('intelligence.allDepartments', { defaultValue: 'All departments' })}</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d.id} value={d.id} data-testid={`intel-dept-opt-${d.id}`}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={openConfig} data-testid="intel-config-btn">
              <Settings2 className="h-4 w-4 mr-1.5" />
              {t('intelligence.configure', { defaultValue: 'Weights' })}
            </Button>
            <Button variant="outline" size="sm" onClick={captureSnapshot} disabled={snapshotsLoading} data-testid="intel-snapshot-btn">
              <Camera className="h-4 w-4 mr-1.5" />
              {t('intelligence.snapshot', { defaultValue: 'Snapshot' })}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="intel-export-btn">
                  <Download className="h-4 w-4 mr-1.5" />
                  {t('intelligence.export', { defaultValue: 'Export' })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild data-testid="intel-export-csv">
                  <a href={adminApi.exportIntelCsvUrl(companyId, departmentId || null)} target="_blank" rel="noreferrer">
                    <FileSpreadsheet className="h-4 w-4 mr-2" /> CSV
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="intel-export-pdf">
                  <a href={adminApi.exportIntelPdfUrl(companyId, departmentId || null, false)} target="_blank" rel="noreferrer">
                    <FileText className="h-4 w-4 mr-2" /> PDF
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="intel-export-pdf-narr">
                  <a href={adminApi.exportIntelPdfUrl(companyId, departmentId || null, true)} target="_blank" rel="noreferrer">
                    <FileText className="h-4 w-4 mr-2" /> PDF + AI narrative
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="default" onClick={generateNarrative} disabled={narrativeLoading} data-testid="intel-narrative-btn">
              {narrativeLoading
                ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                : <Sparkles className="h-4 w-4 mr-1.5 text-accent" />}
              {t('intelligence.generateNarrative', { defaultValue: 'AI summary' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="intel-dashboard">
        {/* Hero — narrative or default summary */}
        <Card className="p-6 md:p-7 shadow-card bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-accent/30 rounded-full blur-3xl" />
          <div className="relative flex items-start gap-6">
            <div className="relative h-28 w-28 shrink-0">
              <svg className="w-full h-full -rotate-90">
                <circle cx="50%" cy="50%" r="48" stroke="rgba(255,255,255,0.15)" strokeWidth="9" fill="none" />
                <circle cx="50%" cy="50%" r="48" stroke="white" strokeWidth="9" fill="none"
                        strokeDasharray={`${(kpis?.avg_value || 0) * 3.01} 999`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display text-3xl font-bold tabular-nums" data-testid="intel-talent-health">
                  {kpis?.avg_value ?? '—'}
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold text-white/70 mt-0.5">
                  {t('intelligence.talentHealth', { defaultValue: 'Avg value' })}
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <Badge className="bg-white/10 border-white/20 text-white hover:bg-white/15">
                <BrainCircuit className="h-3 w-3 mr-1.5" />
                {narrative?.provider === 'claude' ? t('intelligence.aiNarrative') : t('intelligence.crossSuite')}
              </Badge>
              {narrative ? (
                <p className="text-sm md:text-[15px] text-white/90 mt-3 leading-relaxed whitespace-pre-line" data-testid="intel-narrative-text">
                  {narrative.narrative}
                </p>
              ) : (
                <>
                  <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 tracking-tight text-balance">
                    {overview?.company?.name || '…'}
                    <span className="text-white/70 font-normal text-lg md:text-xl ml-2">
                      · {kpis?.employees ?? 0} {t('intelligence.employees', { defaultValue: 'employees' })}
                    </span>
                  </h2>
                  <p className="text-sm text-white/75 mt-3 leading-relaxed">
                    {kpis?.star_count ?? 0} {t('intelligence.stars', { defaultValue: 'star performers' })}
                    {' · '}
                    {kpis?.at_risk_count ?? 0} {t('intelligence.atRisk', { defaultValue: 'at-risk' })}
                    {' · '}
                    {t('intelligence.totalCost', { defaultValue: 'Total loaded cost' })}: {fmtMoney(kpis?.total_cost, ccy)}
                  </p>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* KPI row */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <KpiCard label={t('intelligence.avgValue', { defaultValue: 'Avg value' })}
            value={kpis ? `${kpis.avg_value}/100` : '—'} sub={kpis ? `${kpis.employees} ${t('intelligence.employees', { defaultValue: 'employees' })}` : ''} icon={TrendingUp} tone="primary" testid="kpi-avg-value"
            explainer={t('intelligence.expl.avgValue', { defaultValue: 'Mean value_score (0-100) across all employees in scope · source: intelligence/overview · No dollar conversion is performed' })} />
          <KpiCard label={t('intelligence.totalCost', { defaultValue: 'Total cost' })}
            value={kpis ? fmtMoney(kpis.total_cost, ccy) : '—'} sub={kpis ? `${t('intelligence.median', { defaultValue: 'median' })}: ${fmtMoney(kpis.median_cost, ccy)}` : ''} icon={DollarSign} tone="info" testid="kpi-total-cost"
            explainer={t('intelligence.expl.totalCost', { defaultValue: 'Sum of loaded_cost (salary + benefits + overhead) across all employees · source: user_compensations via intelligence/overview' })} />
          <KpiCard label={t('intelligence.positiveRoi', { defaultValue: 'Positive ROI' })}
            value={kpis ? `${kpis.positive_roi_pct}%` : '—'} sub={t('intelligence.aboveBreakeven', { defaultValue: 'value≥60 & cost≤median' })} icon={Award} tone="success" testid="kpi-roi"
            explainer={t('intelligence.expl.positiveRoi', { defaultValue: '% of employees with value_score ≥ 60 AND loaded_cost ≤ company median · source: intelligence/overview' })} />
          <KpiCard label={t('intelligence.starsLabel', { defaultValue: 'Stars' })}
            value={kpis?.star_count ?? '—'} sub={t('intelligence.highValueLowCost', { defaultValue: 'high value · low cost' })} icon={Star} tone="success" testid="kpi-stars"
            explainer={t('intelligence.expl.stars', { defaultValue: 'Headcount with value_score above the star threshold AND loaded_cost in the lower band · threshold configurable in intelligence/config' })} />
          <KpiCard label={t('intelligence.atRiskLabel', { defaultValue: 'At-risk' })}
            value={kpis?.at_risk_count ?? '—'} sub={t('intelligence.lowValueHighCost', { defaultValue: 'low value · high cost' })} icon={AlertTriangle} tone="destructive" testid="kpi-at-risk"
            explainer={t('intelligence.expl.atRisk', { defaultValue: 'Headcount with value_score below the at-risk threshold AND/OR loaded_cost in the upper band · threshold configurable in intelligence/config' })} />
        </div>

        {/* Scatter (value-vs-cost) + department ranking */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 shadow-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">
                  {t('intelligence.valueVsCost', { defaultValue: 'Value vs cost' })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('intelligence.scatterSubtitle', { defaultValue: 'Each dot is one employee. Top-left = best ROI. Bottom-right = at-risk.' })}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-3 text-xs">
                {Object.entries(QUADRANT_META).map(([k, m]) => (
                  <span key={k} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} />
                    {t(m.label)}
                  </span>
                ))}
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" dataKey="cost" name="cost"
                         tickFormatter={(v) => fmtMoney(v, ccy)}
                         tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                         axisLine={false} tickLine={false} />
                  <YAxis type="number" dataKey="value" domain={[0, 100]} name="value"
                         tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                         axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    formatter={(val, key) => key === 'cost' ? fmtMoney(val, ccy) : `${val}/100`}
                    labelFormatter={() => ''}
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-card border rounded-md p-3 shadow text-xs">
                          <div className="font-semibold text-sm">{d.name}</div>
                          <div>{t('common.valueLabel')} <span className="tabular-nums font-bold">{d.value}/100</span></div>
                          <div>{t('common.costLabel')} <span className="tabular-nums">{fmtMoney(d.cost, ccy)}</span></div>
                          <div className="mt-1">{QUADRANT_META[d.quadrant]?.label ? t(QUADRANT_META[d.quadrant].label) : ''}</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={scatterData}>
                    {scatterData.map((d, i) => (
                      <Cell key={i} fill={QUADRANT_META[d.quadrant]?.color || 'hsl(var(--muted-foreground))'} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6 shadow-card">
            <h3 className="font-display font-bold text-lg tracking-tight">
              {t('intelligence.deptRanking', { defaultValue: 'Department ranking' })}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('intelligence.byAvgValue', { defaultValue: 'by avg value' })} · <span className="text-primary">{t('intelligence.clickToDrill', { defaultValue: 'click a row to drill in' })}</span>
            </p>
            <div className="mt-4 space-y-2" data-testid="intel-dept-rollup">
              {departmentRollup.map(d => {
                const pct = Math.max(2, Math.min(100, d.avg_value));
                const color = d.avg_value >= 75 ? 'bg-success' : d.avg_value >= 60 ? 'bg-primary' : 'bg-warning';
                const deptId = d.department?.id;
                return (
                  <button key={deptId || 'unassigned'}
                    onClick={() => deptId && navigate(`/admin/intelligence/departments/${deptId}`)}
                    disabled={!deptId}
                    className="w-full text-left rounded-lg p-3 border border-transparent hover:border-border hover:bg-muted/40 transition disabled:cursor-default"
                    data-testid={`intel-dept-rollup-${deptId || 'unassigned'}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-medium text-sm">{tData(d.department)}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{d.employee_count} · <span className="font-bold">{d.avg_value}</span>/100</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    {(d.stars > 0 || d.at_risk > 0) && (
                      <div className="flex gap-2 mt-1.5 text-xs">
                        {d.stars > 0 && <span className="text-success">★ {d.stars}</span>}
                        {d.at_risk > 0 && <span className="text-destructive">⚠ {d.at_risk}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
              {!departmentRollup.length && <p className="text-xs text-muted-foreground italic px-3 py-3">{t('intelligence.noDepartments', { defaultValue: 'No departments yet.' })}</p>}
            </div>
          </Card>
        </div>

        {/* Trend over time — same group of collaborators across evaluation cycles (Prompt 15) */}
        {snapshots.length > 1 && (
          <Card className="p-6 shadow-card" data-testid="intel-trend-card">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">
                  {t('intelligence.trend', { defaultValue: 'Evolution over time' })}
                </h3>
                <p className="text-sm text-muted-foreground" data-testid="intel-trend-subtitle">
                  {t('intelligence.trendSubtitle', { defaultValue: 'Average scores of the same team across evaluation cycles.' })}
                </p>
              </div>
              <Badge variant="secondary" className="text-xs">{snapshots.length} {t('intelligence.snapshots', { defaultValue: 'snapshots' })}</Badge>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={snapshots.map(s => ({
                  date: new Date(s.snapshot_date).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
                  avg_value: s.avg_value,
                  positive_roi: s.positive_roi_pct,
                  stars: s.star_count,
                  at_risk: s.at_risk_count,
                  n_evaluated: s.employee_count,
                }))} margin={{ left: -10, right: 20, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload, label }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-card border rounded-md p-3 shadow text-xs space-y-1" data-testid="intel-trend-tooltip">
                          <div className="font-bold text-sm">{label}</div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">{t('intelligence.peopleEvaluated', { defaultValue: 'People evaluated' })}</span>
                            <span className="tabular-nums font-bold">{d.n_evaluated}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">{t('intelligence.avgScore', { defaultValue: 'Average score' })}</span>
                            <span className="tabular-nums font-bold">{d.avg_value}/100</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line type="monotone" dataKey="avg_value" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3 }} name={t('intelligence.avgValue', { defaultValue: 'Avg value' })} />
                  <Line type="monotone" dataKey="positive_roi" stroke="hsl(var(--success))" strokeWidth={2} dot={{ r: 3 }} name={t('intelligence.positiveRoi', { defaultValue: 'Positive ROI' })} />
                  <Line type="monotone" dataKey="at_risk" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 3 }} name={t('intelligence.atRiskLabel', { defaultValue: 'At-risk' })} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}


        {/* Employee table with suggestions */}
        <Card className="p-6 shadow-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {t('intelligence.employeesTable', { defaultValue: 'Employees' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('intelligence.tableSubtitle', { defaultValue: 'Tap a row to see breakdown and recommendations' })}
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              {t('intelligence.median', { defaultValue: 'median' })}: <span className="tabular-nums">{fmtMoney(medianCost, ccy)}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground border-b">
                  <th className="py-2.5 pr-3"><SortableHeader sort={empSort} k="name" testIdPrefix="intel-emp">{t('intelligence.employee', { defaultValue: 'Employee' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={empSort} k="dept" testIdPrefix="intel-emp">{t('intelligence.dept', { defaultValue: 'Dept.' })}</SortableHeader></th>
                  <th className="py-2.5 px-3 text-right"><SortableHeader sort={empSort} k="value" testIdPrefix="intel-emp" align="right">{t('intelligence.value', { defaultValue: 'Value' })}</SortableHeader></th>
                  <th className="py-2.5 px-3 text-right"><SortableHeader sort={empSort} k="cost" testIdPrefix="intel-emp" align="right">{t('intelligence.cost', { defaultValue: 'Cost' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={empSort} k="quadrant" testIdPrefix="intel-emp">{t('intelligence.quadrant', { defaultValue: 'Quadrant' })}</SortableHeader></th>
                  <th className="py-2.5 px-3">{t('intelligence.topAction', { defaultValue: 'Top action' })}</th>
                  <th className="py-2.5 pl-3" />
                </tr>
              </thead>
              <tbody>
                {empSort.apply(employees, {
                  name:     (e) => (e.user?.name || '').toLowerCase(),
                  dept:     (e) => (e.department?.name || '').toLowerCase(),
                  value:    (e) => e.value?.value_score || 0,
                  cost:     (e) => e.cost?.loaded || 0,
                  quadrant: (e) => (e.quadrant || ''),
                }).map(e => {
                  const top = e.suggestions?.[0];
                  const TopIcon = SUGGESTION_META[top?.type]?.icon || ArrowRight;
                  return (
                    <tr key={e.user.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                        onClick={() => setDrilldown(e)} data-testid={`intel-row-${e.user.id}`}>
                      <td className="py-3 pr-3">
                        <div className="font-medium text-foreground">{e.user.name}</div>
                        <div className="text-xs text-muted-foreground">{tData(e.user.position)}</div>
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{tData(e.department)}</td>
                      <td className="py-3 px-3 text-right">
                        <span className="tabular-nums font-bold">{e.value.value_score}</span>
                        <span className="text-muted-foreground">/100</span>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums">{fmtMoney(e.cost.loaded, ccy)}</td>
                      <td className="py-3 px-3">
                        <Badge className={`${QUADRANT_META[e.quadrant]?.tone || 'bg-muted text-muted-foreground'} text-xs`}>
                          {QUADRANT_META[e.quadrant]?.label ? t(QUADRANT_META[e.quadrant].label) : t('intelligence.unevaluated', { defaultValue: 'Sin evaluar' })}
                        </Badge>
                      </td>
                      <td className="py-3 px-3">
                        {top ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <TopIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="truncate max-w-[260px]">{top.title}</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
                          {t('common.review', { defaultValue: 'Review' })}
                          <ArrowRight className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && !employees.length && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">
                    {t('intelligence.noEmployees', { defaultValue: 'No evaluated employees yet for this company.' })}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
        <SuiteDocSection suiteCode="intelligence" />
      </div>

      {/* Drilldown */}
      <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {drilldown?.user?.name}
            </DialogTitle>
            <DialogDescription>
              {tData(drilldown?.user?.position)} · {tData(drilldown?.department)}
            </DialogDescription>
          </DialogHeader>
          {drilldown && (
            <div className="space-y-5">
              {/* Prompt 7: Collaborator status (editable for hm_advisor) */}
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30" data-testid="intel-collab-status">
                <Activity className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('intelligence.collabStatus', { defaultValue: 'Collaborator status' })}</p>
                  {isConsultant ? (
                    <Select
                      value={collabStatus?.status || 'on_track'}
                      onValueChange={(v) => { if (v !== (collabStatus?.status || 'on_track')) setPendingStatus(v); }}
                    >
                      <SelectTrigger className="mt-1 h-8 w-48" data-testid="collab-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on_track" data-testid="collab-status-on_track">{statusText('on_track')}</SelectItem>
                        <SelectItem value="take_action" data-testid="collab-status-take_action">{statusText('take_action')}</SelectItem>
                        <SelectItem value="dismissed" data-testid="collab-status-dismissed">{statusText('dismissed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium mt-1 flex items-center gap-1.5" data-testid="collab-status-readonly">
                      {statusText(collabStatus?.status || 'on_track')}
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </p>
                  )}
                </div>
                {collabStatus?.updated_at && (
                  <div className="text-xs text-muted-foreground text-right shrink-0">
                    <div>{t('intelligence.lastChanged', { defaultValue: 'Last changed' })}</div>
                    <div className="tabular-nums">{new Date(collabStatus.updated_at).toLocaleDateString(i18n.language)}</div>
                    {collabStatus.updated_by_name && <div>{t('intelligence.by', { defaultValue: 'by' })} {collabStatus.updated_by_name}</div>}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Mini label={t('common.value')}  value={`${drilldown.value.value_score}/100`} />
                <Mini label={t('common.cost')}   value={fmtMoney(drilldown.cost.loaded, ccy)} sub={`monthly ${fmtMoney(drilldown.cost.monthly, ccy)}`} />
                <Mini label={t('common.tenure')} value={`${drilldown.tenure_years} ${i18n.language?.startsWith('es') ? 'años' : 'yrs'}`} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  {t('intelligence.valueBreakdown', { defaultValue: 'Value breakdown' })}
                </p>
                <div className="space-y-1.5">
                  {Object.entries(drilldown.value.breakdown).map(([k, b]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="capitalize">{k.replace('_', ' ')} <span className="text-muted-foreground">({b.weight_pct}%)</span></span>
                      <span className="tabular-nums">
                        {b.missing
                          ? <span className="text-destructive">— {t('intelligence.missing', { defaultValue: 'missing' })}</span>
                          : <><span className="font-bold">{b.raw}</span> · <span className="text-muted-foreground">+{b.weighted}</span></>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  {t('intelligence.suggestions', { defaultValue: 'Recommendations' })}
                </p>
                <div className="grid gap-3">
                  {drilldown.suggestions?.map((s, i) => {
                    const Icon = SUGGESTION_META[s.type]?.icon || ArrowRight;
                    const tone = SUGGESTION_META[s.type]?.tone || 'info';
                    const existing = actionsForUser.find(
                      a => a.suggestion_type === s.type && a.suggestion_title === s.title
                    );
                    return (
                      <div key={i} className={`p-4 rounded-lg border ${TONE[tone]}`}>
                        <div className="flex items-start gap-3">
                          <Icon className="h-4 w-4 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{s.title}</p>
                            <p className="text-xs mt-1 leading-relaxed text-foreground/80">{s.detail}</p>
                            <div className="mt-3 flex items-center gap-2">
                              {existing ? (
                                <>
                                  <Badge variant="outline" className="text-xs gap-1">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {existing.status === 'done'
                                      ? t('decisions.done', { defaultValue: 'Done' })
                                      : existing.status === 'in_progress'
                                        ? t('decisions.in_progress', { defaultValue: 'In progress' })
                                        : existing.status === 'dismissed'
                                          ? t('decisions.dismissed', { defaultValue: 'Dismissed' })
                                          : t('decisions.pending', { defaultValue: 'Pending' })}
                                  </Badge>
                                  {existing.status !== 'done' && existing.status !== 'dismissed' && (
                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => updateActionStatus(existing.id, 'done')} data-testid={`intel-mark-done-${i}`}>
                                      {t('intelligence.markDone', { defaultValue: 'Mark done' })}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="default" className="h-7 text-xs" onClick={() => { setPendingSuggestion(s); setPendingAssignee(''); setPendingDueDate(''); }} data-testid={`intel-take-action-${i}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {t('intelligence.takeAction', { defaultValue: 'Take action' })}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => dismissSuggestion(s)} data-testid={`intel-dismiss-${i}`}>
                                    {t('intelligence.dismiss', { defaultValue: 'Dismiss' })}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">P{s.priority}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {actionsForUser.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                    {t('intelligence.decisionsHistory', { defaultValue: 'Decisions for this employee' })}
                  </p>
                  <div className="space-y-2 border-l-2 border-border pl-4">
                    {actionsForUser.slice(0, 6).map(a => (
                      <div key={a.id} className="text-xs">
                        <div className="font-medium">{a.suggestion_title}
                          <Badge variant="outline" className="ml-2 text-xs capitalize">{a.status.replace('_',' ')}</Badge>
                        </div>
                        <div className="text-muted-foreground">{new Date(a.created_at).toLocaleDateString(i18n.language)} · {(a.audit_trail || []).length} {t('decisions.steps', { defaultValue: 'steps' })}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change confirmation (Prompt 7) */}
      <Dialog open={!!pendingStatus} onOpenChange={(o) => !o && setPendingStatus(null)}>
        <DialogContent className="max-w-md" data-testid="collab-status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('intelligence.statusConfirmTitle', { defaultValue: 'Confirm status change' })}</DialogTitle>
            <DialogDescription>
              {i18n.language?.startsWith('es')
                ? `¿Estás seguro de que deseas cambiar el estado a "${statusText(pendingStatus)}"?`
                : `Are you sure you want to change the status to "${statusText(pendingStatus)}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingStatus(null)} data-testid="collab-status-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={confirmStatusChange} disabled={statusSaving} data-testid="collab-status-confirm">
              {statusSaving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.confirm', { defaultValue: 'Confirm' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Take-action dialog (assignee + due date) */}
      <Dialog open={!!pendingSuggestion} onOpenChange={(o) => !o && setPendingSuggestion(null)}>
        <DialogContent className="max-w-md" data-testid="intel-take-action-dialog">
          {pendingSuggestion && (
            <>
              <DialogHeader>
                <DialogTitle>{t('intelligence.logAction', { defaultValue: 'Log decision' })}</DialogTitle>
                <DialogDescription>
                  {pendingSuggestion.title} · {drilldown?.user?.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">{t('intelligence.assignee', { defaultValue: 'Assignee (optional)' })}</Label>
                  <Select value={pendingAssignee || '__none__'} onValueChange={(v) => setPendingAssignee(v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="intel-take-action-assignee"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('intelligence.noAssignee', { defaultValue: 'Unassigned' })}</SelectItem>
                      {assignees.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} <span className="text-muted-foreground text-xs">· {u.role}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('intelligence.dueDate', { defaultValue: 'Due date (optional)' })}</Label>
                  <Input type="date" value={pendingDueDate} onChange={(e) => setPendingDueDate(e.target.value)} data-testid="intel-take-action-due" />
                </div>
                <p className="text-xs text-muted-foreground italic">
                  {pendingSuggestion.detail}
                </p>
              </div>
              <SheetFooter>
                <Button variant="outline" onClick={() => setPendingSuggestion(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button onClick={submitTakeAction} data-testid="intel-take-action-submit">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  {t('intelligence.confirmTake', { defaultValue: 'Log decision' })}
                </Button>
              </SheetFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Config drawer */}
      <Sheet open={configOpen} onOpenChange={setConfigOpen}>
        <SheetContent className="w-[420px] sm:w-[520px]" data-testid="intel-config-sheet">
          <SheetHeader>
            <SheetTitle>{t('intelligence.weightsTitle', { defaultValue: 'Composite weights' })}</SheetTitle>
            <SheetDescription>
              {t('intelligence.weightsDesc', { defaultValue: 'Raw values are auto-normalised. Higher = more weight in the final value index.' })}
            </SheetDescription>
          </SheetHeader>
          {configDraft && (
            <div className="py-5 space-y-4">
              {Object.entries(configDraft.weights).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 items-center gap-3">
                  <Label className="capitalize text-sm">{k.replace('_', ' ')}</Label>
                  <Input type="number" min="0" max="100" value={v}
                    onChange={(e) => setConfigDraft({
                      ...configDraft,
                      weights: { ...configDraft.weights, [k]: Number(e.target.value) || 0 },
                    })}
                    className="col-span-2"
                    data-testid={`intel-weight-${k}`}
                  />
                </div>
              ))}
              <div className="grid grid-cols-3 items-center gap-3 pt-3 border-t">
                <Label className="text-sm">{t('intelligence.tenureCeiling', { defaultValue: 'Tenure ceiling (yrs)' })}</Label>
                <Input type="number" min="1" max="40" value={configDraft.tenure_years_ceiling}
                  onChange={(e) => setConfigDraft({ ...configDraft, tenure_years_ceiling: Number(e.target.value) || 10 })}
                  className="col-span-2" data-testid="intel-tenure-ceiling" />
              </div>
              <div className="grid grid-cols-3 items-center gap-3">
                <Label className="text-sm">{t('intelligence.currency', { defaultValue: 'Currency' })}</Label>
                <Input value={configDraft.currency || 'USD'}
                  onChange={(e) => setConfigDraft({ ...configDraft, currency: e.target.value || 'USD' })}
                  className="col-span-2" data-testid="intel-currency" />
              </div>
              {!configDraft.is_override && (
                <p className="text-xs text-muted-foreground italic">
                  {t('intelligence.usingGlobal', { defaultValue: 'Currently inheriting the global default. Saving will create a per-company override.' })}
                </p>
              )}
            </div>
          )}
          <SheetFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={saveConfig} data-testid="intel-config-save">{t('common.save', { defaultValue: 'Save' })}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone, testid, explainer }) {
  return (
    <Card className="p-5 shadow-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <div className="flex items-center gap-1.5">
          {explainer ? (
            <TooltipProvider delayDuration={150}>
              <UiTooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={explainer}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={testid ? `${testid}-info` : undefined}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  className="max-w-xs text-xs leading-snug"
                  data-testid={testid ? `${testid}-tooltip` : undefined}
                >
                  {explainer}
                </TooltipContent>
              </UiTooltip>
            </TooltipProvider>
          ) : null}
          <Icon className={`h-4 w-4 text-${tone}`} />
        </div>
      </div>
      <p className="font-display text-2xl font-bold tracking-tight tabular-nums mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function Mini({ label, value, sub }) {
  return (
    <div className="p-3 rounded-lg bg-muted/40">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="font-display text-lg font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
